/**
 * PortfolioBreadcrumb — Feb 2026 · Iter88
 *
 * Slim, always-visible breadcrumb rendered by <ProjectStageLayout /> at
 * the top of every 9-stage page:
 *
 *   All projects  ›  CHUNYA GOLD  ›  Stage 6 · Estimation Settings
 *
 * The stage label + number are resolved from the current URL via
 * `resolveFlowKey`, so it stays in lock-step with the sidebar and the
 * stage headers without any per-page wiring.
 */
import { useMemo } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CaretRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { WORKFLOW_STAGES, resolveFlowKey } from "@/config/simplified_flow";

export default function PortfolioBreadcrumb() {
  const { pid } = useParams();
  const { pathname } = useLocation();

  const projectQ = useQuery({
    queryKey: ["project", pid],
    queryFn: () => api.get(`/projects/${pid}`).then((r) => r.data),
    enabled: !!pid,
    staleTime: 60_000,
  });

  const stage = useMemo(() => {
    const k = resolveFlowKey(pathname);
    return WORKFLOW_STAGES.find((s) => s.key === k);
  }, [pathname]);

  const projectName = projectQ.data?.name || "…";

  return (
    <nav className="shrink-0 flex items-center gap-1.5 px-6 py-1.5 bg-white
                     border-b border-slate-200 text-[11px] font-mono
                     text-slate-500 min-h-[26px]"
             data-testid="portfolio-breadcrumb">
      <Link
        to="/app/projects"
        className="uppercase tracking-widest hover:text-brand-blue"
        data-testid="portfolio-breadcrumb-projects"
      >
        All projects
      </Link>
      <CaretRight size={9} className="text-slate-300" />
      <span
        className="uppercase tracking-widest truncate max-w-[280px] text-slate-700"
        data-testid="portfolio-breadcrumb-project"
        title={projectName}
      >
        {projectName}
      </span>
      {stage && (
        <>
          <CaretRight size={9} className="text-slate-300" />
          <span className="uppercase tracking-widest text-brand-blue"
                data-testid="portfolio-breadcrumb-stage">
            Stage {stage.n} · {stage.label}
          </span>
        </>
      )}
    </nav>
  );
}
